import { useState } from "react";
import { useForm } from "react-hook-form";
import { primaryCtaClass } from "../utils/ctaClasses";

const DEMO_RECIPIENT = "admin@gestalttechindia.in";

const COUNTRIES = [
  { value: "", label: "Country" },
  { value: "us", label: "United States" },
  { value: "uk", label: "United Kingdom" },
  { value: "in", label: "India" },
  { value: "ae", label: "United Arab Emirates" },
  { value: "sg", label: "Singapore" },
  { value: "eu", label: "European Union" },
  { value: "other", label: "Other" },
];

const ROLE_LABELS = {
  administrator: "Administrator",
  doctor: "Physician / Clinician",
  operations: "Operations / IT",
  executive: "Executive Leadership",
  other: "Other",
};

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-3 text-sm text-secondary outline-none transition placeholder:text-slate-400 focus:border-accent/55 focus:bg-white focus:ring-2 focus:ring-accent/20";

export default function DemoForm() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const [status, setStatus] = useState("idle");

  const onSubmit = async (data) => {
    setStatus("submitting");

    const countryLabel =
      COUNTRIES.find((c) => c.value === data.country)?.label || data.country;
    const roleLabel = ROLE_LABELS[data.role] || data.role;

    try {
      const response = await fetch(
        `https://formsubmit.co/ajax/${encodeURIComponent(DEMO_RECIPIENT)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            name: data.name,
            organization: data.organization,
            role: roleLabel,
            email: data.email,
            phone: data.phone,
            country: countryLabel,
            message: data.message || "(none)",
            _subject: "New Demo Request – Gestalt HIS Website",
            _template: "table",
            _captcha: "false",
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Submission failed");
      }

      setStatus("success");
      reset();
    } catch {
      setStatus("error");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <input
            {...register("name", { required: "Full name is required" })}
            placeholder="Full Name"
            className={inputClass}
            autoComplete="name"
          />
          {errors.name ? (
            <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>
          ) : null}
        </div>
        <div>
          <input
            {...register("organization", {
              required: "Organization is required",
            })}
            placeholder="Organization / Hospital"
            className={inputClass}
          />
          {errors.organization ? (
            <p className="mt-1 text-xs text-red-600">
              {errors.organization.message}
            </p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <select
          {...register("role", { required: "Please select a role" })}
          defaultValue=""
          className={inputClass}
        >
          <option value="" disabled>
            Role / Designation
          </option>
          <option value="administrator">Administrator</option>
          <option value="doctor">Physician / Clinician</option>
          <option value="operations">Operations / IT</option>
          <option value="executive">Executive Leadership</option>
          <option value="other">Other</option>
        </select>
        <div>
          <input
            {...register("email", {
              required: "Work email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Enter a valid email address",
              },
            })}
            type="email"
            placeholder="Work Email"
            className={inputClass}
            autoComplete="email"
          />
          {errors.email ? (
            <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input
          {...register("phone")}
          type="tel"
          placeholder="Phone Number"
          className={inputClass}
          autoComplete="tel"
        />
        <select
          {...register("country", { required: "Please select a country" })}
          defaultValue=""
          className={inputClass}
        >
          {COUNTRIES.map((c) => (
            <option
              key={c.value || "empty"}
              value={c.value}
              disabled={c.value === ""}
            >
              {c.label}
            </option>
          ))}
        </select>
      </div>

      <textarea
        {...register("message")}
        placeholder="Tell us about your goals or questions (optional)"
        rows={4}
        className={`${inputClass} resize-none`}
      />

      <button
        type="submit"
        disabled={status === "submitting"}
        className={`${primaryCtaClass} w-full py-3.5 text-base font-semibold disabled:cursor-not-allowed disabled:opacity-70`}
      >
        {status === "submitting" ? "Sending..." : "Request Demo"}
      </button>

      {status === "success" ? (
        <p className="text-center text-sm font-medium text-emerald-700">
          Thank you! Your request has been sent. We will be in touch soon.
        </p>
      ) : null}

      {status === "error" ? (
        <p className="text-center text-sm font-medium text-red-600">
          Something went wrong. Please try again or email us at{" "}
          <a
            href={`mailto:${DEMO_RECIPIENT}`}
            className="underline underline-offset-2"
          >
            {DEMO_RECIPIENT}
          </a>
          .
        </p>
      ) : null}

      <p className="text-center text-xs leading-relaxed text-muted">
        We respect your privacy. Your information is safe with us.
      </p>
    </form>
  );
}
